import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

interface UseResizePanelOptions {
	containerRef: RefObject<HTMLDivElement | null>;
	defaultLeftFraction?: number;
	minLeftFraction?: number;
	maxLeftFraction?: number;
	availableWidthOffset?: number;
}

interface UseResizePanelResult {
	leftFraction: number;
	handleMouseDown: (e: React.MouseEvent) => void;
	isDragging: boolean;
}

export const useResizePanel = ({
	containerRef,
	defaultLeftFraction = 0.5,
	minLeftFraction = 0.3,
	maxLeftFraction = 0.7,
	availableWidthOffset = 0,
}: UseResizePanelOptions): UseResizePanelResult => {
	const [leftFraction, setLeftFraction] = useState(defaultLeftFraction);
	const [isDragging, setIsDragging] = useState(false);
	const dragStartRef = useRef<{ startX: number; startFraction: number } | null>(
		null,
	);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			dragStartRef.current = { startX: e.clientX, startFraction: leftFraction };
			setIsDragging(true);
		},
		[leftFraction],
	);

	useEffect(() => {
		if (!isDragging) return;

		document.body.style.cursor = 'col-resize';
		document.body.style.userSelect = 'none';

		const handleMouseMove = (e: MouseEvent) => {
			const container = containerRef.current;
			const dragStart = dragStartRef.current;
			if (!container || !dragStart) return;

			const effectiveWidth =
				container.getBoundingClientRect().width - availableWidthOffset;
			if (effectiveWidth <= 0) return;

			const deltaX = e.clientX - dragStart.startX;
			const deltaFraction = deltaX / effectiveWidth;
			const newFraction = Math.min(
				maxLeftFraction,
				Math.max(minLeftFraction, dragStart.startFraction + deltaFraction),
			);
			setLeftFraction(newFraction);
		};

		const handleMouseUp = () => {
			setIsDragging(false);
			dragStartRef.current = null;
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};
	}, [
		isDragging,
		containerRef,
		minLeftFraction,
		maxLeftFraction,
		availableWidthOffset,
	]);

	// Reset to default when dependencies change externally
	useEffect(() => {
		setLeftFraction(defaultLeftFraction);
	}, [defaultLeftFraction]);

	return { leftFraction, handleMouseDown, isDragging };
};
